import { FC, useEffect, useMemo, useState } from 'react';
import { Calendar, View, Views } from 'react-big-calendar';
import { DragItem } from 'src/constants/drag-and-drop';
import { useDrop } from 'react-dnd';
import localizer, { getCulture } from '../../../common/ShsCalendar/config';
import 'react-big-calendar/lib/addons/dragAndDrop/styles.css';
import { Toolbar } from 'src/components/common/ShsCalendar/components';
import { ScheduleEventCard, ScheduleMonthEventCard } from '../../common/components/ScheduleEventCard';
import { styled } from '@mui/system';
import { ScheduleCalendarEvent } from './ScheduleCalendar.types';
import { DragAndDropData } from '../../common/interfaces/DragAndDrop.interface';
import { useWatch } from 'react-hook-form';
import { EducationLevelGroup, EducationStage } from 'src/constants';
import { useScheduleCalendarContext } from '../../common/context/CalendarContext';
import { ScheduleBooking } from '../../common/interfaces/ScheduleBooking.interface';
import { TimeSlotWithDrop } from './TimeSlotWithDrop';
import { UserRoleToLessonTypeMap } from '../../common/const/ScheduleCalendar.const';
import { EducationCenterType, Hospital, LessonsDuration, LessonsDurationByLevel } from 'src/components/Employees/types';
import { UserRole } from 'src/auth/types';
import { LessonOrder } from 'src/components/Employees/enums';
import { ScheduleChild } from '../../common/interfaces/ScheduleChild.interface';
import { ScheduleEmployee } from '../../common/interfaces/ScheduleEmployee.interface';
import { ScheduleSubject } from '../../common/interfaces/ScheduleSubject.interface';
import { useNotify } from 'react-admin';
import DateTimeUtils from 'src/utils/datetime';
import { LessonStatus } from '../../common/enums/lesson.enum';
import axiosApiInstance from 'src/api/axiosConfig';
import { ScheduleLesson } from '../../common/interfaces/ScheduleLesson.interface';
import { EducationGroupOldToNewMap } from '../../common/const/EducationGroup.const';
import { ShsCalendarEventEditDialog } from './EditCalendarEventDialog';
import { Theme } from 'src/Theme';
import { ScheduleManager } from 'src/managers/Schedule/ScheduleManager';

interface ScheduleCalendarProps {
    events: ScheduleCalendarEvent[];
    activeTab: EducationLevelGroup;
    refetchEvents: () => void;
}

interface LessonData {
    date: Date;
    role: UserRole;
    lessonOrder: LessonOrder;
    children: ScheduleChild[];
    employee: ScheduleEmployee;
    subject: ScheduleSubject;
    educationCenter: EducationCenterType;
    hospital: Hospital;
    notes?: string;
    meetingLink?: string;
    classroomId?: string;
    durationMinutes?: number;
    consultationType?: string;
    isSpecialTeacher?: boolean;
}
function getShowMoreLabel(count: number): string {
    const lastDigit = count % 10;
    let postfix = 'ів';
    if (lastDigit === 1) {
        postfix = '';
    } else if (lastDigit >= 2 && lastDigit <= 4) {
        postfix = 'и';
    }

    return `${lastDigit} урок${postfix}`;
}

const messages = {
    showMore: (total: number) => `+${getShowMoreLabel(total)}`
};

const getStartEndTime = (timeStr?: string, isStart?: boolean): Date => {
    const isValidTime = /^([0-1]?\d|2[0-3]):[0-5]?\d(:[0-5]?\d)?$/;

    if (timeStr && isValidTime.test(timeStr)) {
        const [hours, minutes = 0, seconds = 0] = timeStr.split(':').map(Number);
        return new Date(0, 0, 0, hours, minutes, seconds);
    }

    return new Date(0, 0, 0, isStart ? 8 : 18, 0, 0);
};

async function createLesson(data: LessonData): Promise<ScheduleLesson> {
    const {
        date,
        role,
        lessonOrder,
        children,
        employee,
        subject,
        educationCenter,
        hospital,
        notes,
        meetingLink,
        classroomId,
        durationMinutes,
        consultationType,
        isSpecialTeacher
    } = data;
    const educationLevels = children.flatMap((child) => child.educationLevel);

    const body: any = {
        children: children.map((child) => ({ childId: child.id, online: !!meetingLink })),
        lessonNumber: lessonOrder,
        employeeId: employee.id,
        subjectId: subject.id,
        educationCenterId: educationCenter.id,
        hospitalId: hospital.id,
        lessonDate: DateTimeUtils.getFormattedDate(date),
        status: LessonStatus.PLANNED,
        educationLevels,
        notes,
        meetingLink,
        classroomId,
        type: UserRoleToLessonTypeMap[role],
        durationMinutes: durationMinutes,
        consultationType: consultationType
    };
    if (durationMinutes) {
        body.startTime = DateTimeUtils.getTimeWithSeconds(date);
        body.endTime = DateTimeUtils.addMinutesAndFormat(date, durationMinutes);
    }
    if (isSpecialTeacher) {
        body.isSpecialTeacher = true;
    }

    return axiosApiInstance.post('lessons/add', body);
}

async function updateLesson(
    data: ScheduleCalendarEvent,
    lessonOrder: LessonOrder,
    slotDate: Date
): Promise<ScheduleLesson> {
    const lesson = await axiosApiInstance.get(`lessons/${data.id}`).then((data) => data.data);
    const body = {
        ...lesson,
        ...data,
        lessonNumber: lessonOrder,
        lessonDate: DateTimeUtils.getFormattedDate(slotDate)
    };

    if (lesson?.subject?.durationMinutes) {
        body.startTime = DateTimeUtils.getTimeWithSeconds(slotDate);
        body.endTime = DateTimeUtils.addMinutesAndFormat(slotDate, lesson.subject.durationMinutes);
    } else {
        delete body.startTime;
        delete body.endTime;
    }
    return axiosApiInstance.put('lessons/update', body).then((data) => data.data);
}

const CalendarCellDimensions = {
    width: 150,
    height: 78
};

const getStepAndTimeslots = (duration: number): { step: number; timeslots: number } => {
    switch (duration) {
        case 10:
        case 15:
        case 20:
        case 30:
            return { step: duration, timeslots: 1 };

        case 25:
            return { step: 5, timeslots: 5 };

        case 35:
            return { step: 7, timeslots: 5 };

        case 40:
            return { step: 20, timeslots: 2 };

        case 45:
            return { step: 15, timeslots: 3 };

        default:
            return { step: 15, timeslots: Math.floor(duration / 15) };
    }
};

const StyledCalendar = styled(Calendar)<{ $viewMode?: boolean; $isDragging?: boolean }>(
    ({ $viewMode, $isDragging }) => ({
        minHeight: '800px',
        minWidth: `calc(100vw - ${$viewMode ? '376px' : '656px'} )`, // Adjusts for sidebars 376px (page sidebar only) or 656px (with schedule sidebar)
        '.rbc-event': {
            backgroundColor: 'transparent',
            pointerEvents: `${$isDragging ? 'none' : ''}`,
            border: 'none',
            padding: '4px',
            '&.rbc-selected:focus': {
                outline: '5px auto currentColor'
            },
            '> div': {
                opacity: `${$isDragging ? '0.5' : ''}`
            }
        },
        '.rbc-time-view': {
            minWidth: '600px',
            '.rbc-event-label': {
                display: 'none'
            },
            '.rbc-events-container': {
                position: 'static'
            },
            '.rbc-timeslot-group': {
                flexBasis: 'auto',
                height: CalendarCellDimensions.height,
                minWidth: CalendarCellDimensions.width
            },
            '.rbc-time-header-gutter': {
                minWidth: CalendarCellDimensions.width
            },
            '.rbc-day-bg': {
                minWidth: CalendarCellDimensions.width - 1 // border
            },
            '.rbc-header': {
                minWidth: CalendarCellDimensions.width - 1 // border
            }
        },
        '.rbc-month-view': {
            minWidth: '800px'
        },
        '.rbc-show-more': {
            color: Theme.palette.primary.main,
            margin: 'auto'
        }
    })
);

export const ScheduleCalendar: FC<ScheduleCalendarProps> = ({ events, activeTab, refetchEvents }) => {
    const { calendarData, calendarConfig, setCalendarConfig, setCalendarData } = useScheduleCalendarContext();
    const [calendarEvents, setCalendarEvents] = useState<ScheduleCalendarEvent[]>([]);
    const [dialogEvent, setDialogEvent] = useState<ScheduleCalendarEvent | null>();
    const [view, setView] = useState<View>(Views.WEEK);
    const notify = useNotify();

    const lessonsDuration = useMemo(() => {
        const groupKey = EducationGroupOldToNewMap[activeTab] as keyof LessonsDuration;
        const duration = calendarData?.hospital?.lessonsDuration?.[groupKey]?.lessonDuration;

        return duration ?? 20;
    }, [calendarData, activeTab]);

    const selectedEducationGroupLevels = useWatch({
        name: 'educationLevelGroup',
        defaultValue: [EducationStage.YEARS4, EducationStage.YEARS5, EducationStage.YEARS6]
    });

    const bookingInfo: ScheduleBooking[] = [];
    if (calendarData?.children) {
        calendarData.children.forEach((child) => bookingInfo.push(...child.bookingInfo));
    }
    if (calendarData?.employee) {
        bookingInfo.push(...calendarData.employee.bookingInfo);
    }
    let scheduleSlots: LessonsDurationByLevel['weekdays'];
    if (calendarData?.hospital) {
        const oldEducationGroupName = EducationGroupOldToNewMap[calendarConfig.educationLevelGroup];

        // @ts-ignore // todo: correct this once backend is aligned with FE in education level naming
        scheduleSlots = calendarData.hospital.lessonsDuration[oldEducationGroupName].weekdays;
    }

    useEffect(() => {
        if (calendarConfig.viewMode) {
            events = events.filter((calendarEvent) =>
                calendarEvent.educationLevels?.some((value) => selectedEducationGroupLevels.includes(value))
            );
        }
        setCalendarEvents(Array.isArray(events) ? events : []);
    }, [events, selectedEducationGroupLevels, calendarConfig]);

    function handleScheduleEventDoubleClick(event: ScheduleCalendarEvent) {
        setDialogEvent(event);
    }

    const handleTimeslotDrop = async (dropEvent: ScheduleCalendarEvent, lessonOrder: LessonOrder, slotDate: Date) => {
        const droppedEvent = events.find((x) => x.id === dropEvent.id);
        if (droppedEvent) {
            setCalendarEvents((calendarEvents) =>
                calendarEvents.filter((calendarEvent) => calendarEvent.id !== dropEvent.id)
            );
            updateExistingLesson(droppedEvent, lessonOrder, slotDate);
        } else {
            createNewLesson(dropEvent, lessonOrder, slotDate);
        }
    };

    const createNewLesson = async (dropEvent: ScheduleCalendarEvent, lessonOrder: LessonOrder, slotDate: Date) => {
        const { educationCenter, hospital, role } = calendarData;
        // events can be dropped outside of the calendar as well as dragged within
        const children = dropEvent.children || calendarData.children;
        const employee = dropEvent.employee || calendarData.employee;
        const subject = dropEvent.subject || calendarData.subject;

        if (!children || !employee || !subject || !educationCenter || !role) {
            throw new Error('Not enough data to create lesson');
        }

        try {
            const newLesson = await createLesson({
                date: slotDate,
                role,
                lessonOrder,
                children,
                employee,
                subject,
                educationCenter,
                hospital: hospital as Hospital,
                meetingLink: dropEvent.meetingLink as string,
                classroomId: dropEvent.classroomId as string,
                durationMinutes: dropEvent.durationMinutes,
                consultationType: dropEvent.consultationType,
                isSpecialTeacher: dropEvent.isSpecialTeacher
            });
            setCalendarData((data) => ({
                ...data,
                newLesson
            }));
        } catch (error: any) {
            console.error(error);
            notify(error.message, { type: 'error' });
        }
    };

    const updateExistingLesson = async (dropEvent: ScheduleCalendarEvent, lessonOrder: LessonOrder, slotDate: Date) => {
        try {
            const updatedLesson = await updateLesson(dropEvent, lessonOrder, slotDate);
            setCalendarData((data) => ({
                ...data,
                newLesson: updatedLesson
            }));
        } catch (error: any) {
            console.error(error);
            notify(error.message, { type: 'error' });
        }
    };

    const handleRangeChange = (range: Date[] | { start: Date; end: Date }) => {
        let start: Date;
        let end: Date;

        if (Array.isArray(range)) {
            // week view
            start = range[0];
            end = range[range.length - 1];
        } else {
            // month, agenda view
            start = range.start;
            end = range.end;
        }

        setCalendarConfig((config) => ({ ...config, range: { start, end } }));
    };

    const { step, timeslots } = getStepAndTimeslots(+lessonsDuration);

    const [{ isOver }, dropRef] = useDrop({
        accept: DragItem.SCHEDULE_CARD,
        collect: (monitor) => ({
            isOver: monitor.isOver()
        })
    });

    // Check calendar events to find which are in the same time slot
    calendarEvents.map((event) => {
        calendarEvents.map((nextEvent) => {
            if (event !== nextEvent && event.start && event.end && nextEvent.start && nextEvent.end) {
                const isEventInSameTimeSlot = event.start < nextEvent.end && nextEvent.start < event.end;

                if (isEventInSameTimeSlot) {
                    event.isEventInSameTimeSlot = true;
                    nextEvent.isEventInSameTimeSlot = true;
                }
            }
        });
    });

    return (
        <>
            <div ref={dropRef}>
                <StyledCalendar
                    $isDragging={isOver}
                    $viewMode={calendarConfig.viewMode}
                    localizer={localizer}
                    events={calendarEvents}
                    selectable={false}
                    view={view}
                    onView={(newView) => setView(newView)}
                    timeslots={timeslots}
                    step={step}
                    min={getStartEndTime(calendarData.hospital?.workStart, true)}
                    max={getStartEndTime(calendarData.hospital?.workEnd)}
                    formats={{
                        timeGutterFormat: 'HH:mm'
                    }}
                    culture={getCulture('uk')}
                    messages={messages}
                    onRangeChange={handleRangeChange}
                    components={{
                        toolbar: (props) => (
                            <Toolbar {...props} activeTab={calendarConfig.viewMode ? activeTab : undefined} />
                        ),
                        event: ({ event }) => {
                            const isMonthView = view === Views.MONTH;

                            const calendarEvent = event as ScheduleCalendarEvent;

                            return isMonthView ? (
                                <ScheduleMonthEventCard event={calendarEvent} />
                            ) : (
                                <ScheduleEventCard
                                    event={calendarEvent}
                                    onDoubleClick={handleScheduleEventDoubleClick}
                                    allowDelete
                                />
                            );
                        },
                        timeSlotWrapper: (props) => (
                            <TimeSlotWithDrop
                                {...props}
                                onDrop={handleTimeslotDrop}
                                bookingInfo={bookingInfo}
                                scheduleSlots={scheduleSlots}
                            />
                        )
                    }}
                />
            </div>
            {dialogEvent && ScheduleManager.isCalendarEventEdit() && (
                <ShsCalendarEventEditDialog
                    open={!!dialogEvent}
                    dialogEvent={dialogEvent}
                    onClose={() => setDialogEvent(null)}
                    refetchEvents={refetchEvents}
                />
            )}
        </>
    );
};
